import React from 'react';
import { PortalDataState } from '../components/PortalDataState';
import { PortalStatusPill } from '../components/PortalStatusPill';
import type { CollaborationShareInvitation } from './collaborationControlPlaneShared';
import { selfAssessmentLifecycleOf } from '../services/collaboration/collaborationShareKind';
import { shareStatusPresentation } from '../services/collaboration/supplierCollaborationVocabulary';
import type { CollaborationEvidenceSummary } from './collaborationWorkQueueLogic';

interface CollaborationSentSharesCardProps {
  shares: CollaborationShareInvitation[];
  partnerNameById: Map<string, string>;
  revokingShareId: string;
  threadLoadingShareId: string;
  newShareIds: ReadonlySet<string>;
  evidenceByShareId: ReadonlyMap<string, CollaborationEvidenceSummary>;
  relativeTime: (value: string) => string;
  parseJsonSafe: (value: string) => Record<string, unknown>;
  onRevokeShare: (shareInvitationId: string) => void;
  onOpenThread: (shareInvitationId: string) => void;
}

const CollaborationSentSharesCard = ({
  shares,
  partnerNameById,
  revokingShareId,
  threadLoadingShareId,
  newShareIds,
  evidenceByShareId,
  relativeTime,
  parseJsonSafe,
  onRevokeShare,
  onOpenThread
}: CollaborationSentSharesCardProps): JSX.Element => (
  <section className="card collaboration-work-queue" aria-labelledby="sent-work-heading">
    <div className="collaboration-section-heading">
      <div>
        <h2 id="sent-work-heading" tabIndex={-1}>Sent work</h2>
        <p className="muted">Work initiated by your auditors, with its current supplier response and evidence state.</p>
      </div>
      <PortalStatusPill label={`${shares.length} total`} />
    </div>
    {shares.length === 0 ? (
      <PortalDataState
        compact
        kind="first-empty"
        heading="No sent work yet"
        message="Supplier work shared from Edinshall Audit will appear here."
      />
    ) : (
    <div className="collaboration-work-list">
      {shares.map((share) => {
        const payload = parseJsonSafe(share.payloadJson);
        const isSelfAssessment = share.kind === 'self_assessment';
        const lifecycle = isSelfAssessment ? selfAssessmentLifecycleOf(share) : null;
        const canRevoke = share.status === 'pending' || share.status === 'accepted';
        const isLoading = threadLoadingShareId === share.shareInvitationId;
        const status = shareStatusPresentation(share.status);
        const evidence = evidenceByShareId.get(share.shareInvitationId);
        const isNew = newShareIds.has(share.shareInvitationId);
        return (
          <article className="collaboration-work-row" key={share.shareInvitationId}>
            <button
              className="collaboration-work-row__main"
              type="button"
              onClick={() => onOpenThread(share.shareInvitationId)}
              disabled={isLoading}
              aria-label={`Open ${String(payload.title || 'sent supplier work')}`}
            >
              <span className="collaboration-work-row__title-line">
              <strong>{String(payload.title || (isSelfAssessment ? 'Supplier self-assessment' : 'Shared finding'))}</strong>
                {isNew && <PortalStatusPill label="New" tone="warning" />}
              </span>
              {isSelfAssessment ? (
                <span className="muted">Self-assessment questionnaire · {String(payload.templateKey || payload.auditTemplateKey || 'template unavailable')}</span>
              ) : (
                <span className="muted">Finding {String(payload.findingId || 'reference unavailable')}</span>
              )}
              {share.partnerId && (
                <span className="muted">Supplier · {partnerNameById.get(share.partnerId) || 'Supplier record'}</span>
              )}
              <span className="muted">Updated {relativeTime(share.updatedAt)} · expires {new Date(share.expiresAt).toLocaleString()}</span>
              <span className="collaboration-work-row__status-line">
                <PortalStatusPill label={status.label} tone={status.tone} />
              {isSelfAssessment && lifecycle && (
                  <PortalStatusPill label={`Questionnaire ${lifecycle}`} tone={lifecycle === 'submitted' ? 'success' : 'neutral'} />
              )}
              </span>
              <span className="muted">{evidence?.label || 'Evidence details available when the thread opens'}</span>
              <span className="collaboration-work-row__open">{isLoading ? 'Opening…' : 'Open thread →'}</span>
            </button>
            <div className="collaboration-work-row__actions">
              {canRevoke && (
                <button
                  className="btn secondary"
                  type="button"
                  onClick={() => onRevokeShare(share.shareInvitationId)}
                  disabled={revokingShareId === share.shareInvitationId}
                >
                  {revokingShareId === share.shareInvitationId ? 'Revoking…' : 'Revoke'}
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
    )}
  </section>
);

export default CollaborationSentSharesCard;
